    /**
 * Health Service
 * Handles health checks, debugging, and API testing
 */

import { CHATBOT_API_BASE_URL, MAIN_API_BASE_URL } from '../utils/apiConfig';
import { getAccessToken} from '../utils/authHelper';
import { chatService } from './chatService';

export const healthService = {
    /**
     * Health check for chatbot backend
     * @returns {Promise<Object>} Health status
     */
    async healthCheck() {
        try {
            const response = await fetch(
                `${CHATBOT_API_BASE_URL}/health`,
                {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(
                    `Health check failed: HTTP ${response.status}`
                );
            }

            const result = await response.json();
            return {
                status: "healthy",
                timestamp: new Date().toISOString(),
                ...result,
            };
        } catch (error) {
            console.error("Chatbot health check failed:", error);
            return {
                status: "unhealthy",
                error: error.message,
                timestamp: new Date().toISOString(),
            };
        }
    },

    /**
     * Check if Main Backend is accessible
     * @returns {Promise<Object>} Backend status
     */
    async checkMainBackend() {
        try {
            const token = getAccessToken();
            const headers = {
                "Content-Type": "application/json",
            };

            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }

            // Try multiple health endpoints
            const healthEndpoints = [
                "/health",
                "/api/health",
                "/status",
            ];

            for (const endpoint of healthEndpoints) {
                try {
                    const response = await fetch(
                        `${MAIN_API_BASE_URL}${endpoint}`,
                        {
                            method: "GET",
                            headers: headers,
                        }
                    );

                    return {
                        status: response.ok ? "healthy" : "unhealthy",
                        statusCode: response.status,
                        endpoint: endpoint,
                        authenticated: !!token,
                        timestamp: new Date().toISOString(),
                    };
                } catch (error) {
                    continue; // Try next endpoint
                }
            }

            throw new Error("All health endpoints failed");
        } catch (error) {
            return {
                status: "unhealthy",
                error: error.message,
                authenticated: !!getAccessToken(),
                timestamp: new Date().toISOString(),
            };
        }
    },

    /**
     * Get current API configuration
     * @returns {Object} API configuration
     */
    getConfig() {
        return {
            chatbotBaseUrl: CHATBOT_API_BASE_URL,
            mainBackendBaseUrl: MAIN_API_BASE_URL,
            hasAccessToken: !!getAccessToken(),
            timestamp: new Date().toISOString(),
        };
    },

    /**
     * Test connection to both backends
     * @returns {Promise<Object>} Connection test results
     */
    async testConnections() {
        try {
            // Test chatbot with a simple message
            let chatbotResult;
            try {
                const testResponse = await chatService.sendMessage(
                    "Hello, are you working?",
                    "test"
                );
                chatbotResult = {
                    status: "healthy",
                    tested: true,
                    response:
                        testResponse?.response ||
                        "No response",
                };
            } catch (error) {
                chatbotResult = {
                    status: "unhealthy",
                    error: error.message,
                };
            }

            // Test main backend
            const mainBackendResult = await this.checkMainBackend();

            const overall =
                chatbotResult.status === "healthy" &&
                mainBackendResult.status === "healthy"
                    ? "healthy"
                    : chatbotResult.status === "healthy" ||
                      mainBackendResult.status === "healthy"
                    ? "partial"
                    : "unhealthy";

            const result = {
                chatbot: chatbotResult,
                mainBackend: mainBackendResult,
                overall: overall,
                timestamp: new Date().toISOString(),
            };

            return result;
        } catch (error) {
            const result = {
                chatbot: {
                    status: "unhealthy",
                    error: error.message,
                },
                mainBackend: { status: "unknown" },
                overall: "unhealthy",
                timestamp: new Date().toISOString(),
            };

            console.error("❌ Connection test failed:", result);
            return result;
        }
    },

    /**
     * Debug helper - test known working endpoints
     * @returns {Promise<Object>} Debug test results
     */
    async debugEndpoints() {
        const results = {
            chatbot: {},
            mainBackend: {},
        };

        // Test known working chatbot endpoints
        const chatbotEndpoints = [
            "/health",
            "/api/chat",
            "/api/collections",
            "/api/current-question",
            "/chat",
            "/docs",
        ];

        // Test main backend endpoints
        const mainBackendEndpoints = [
            "/health",
            "/api/health",
            "/questions/test",
            "/api/questions/test",
        ];

        // Test chatbot endpoints
        for (const endpoint of chatbotEndpoints) {
            try {
                const method = endpoint === "/api/chat" ? "POST" : "GET";
                const body =
                    endpoint === "/api/chat"
                        ? JSON.stringify({ question: "test" })
                        : undefined;

                const response = await fetch(
                    `${CHATBOT_API_BASE_URL}${endpoint}`,
                    {
                        method,
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body,
                    }
                );

                results.chatbot[endpoint] = {
                    status: response.status,
                    ok: response.ok,
                    contentType: response.headers.get("content-type"),
                };
            } catch (error) {
                results.chatbot[endpoint] = {
                    error: error.message,
                };
            }
        }

        // Test main backend endpoints
        const token = getAccessToken();
        for (const endpoint of mainBackendEndpoints) {
            try {
                const headers = {
                    "Content-Type": "application/json",
                };
                if (token) {
                    headers["Authorization"] = `Bearer ${token}`;
                }

                const response = await fetch(
                    `${MAIN_API_BASE_URL}${endpoint}`,
                    { headers }
                );

                results.mainBackend[endpoint] = {
                    status: response.status,
                    ok: response.ok,
                    authenticated: !!token,
                    contentType: response.headers.get("content-type"),
                };
            } catch (error) {
                results.mainBackend[endpoint] = {
                    error: error.message,
                };
            }
        }

        return results;
    },
};
